/**
 * Section links from the announcement bar: getting a visitor to `#anchor` on a
 * page they may not be on yet.
 *
 * Why not hand the router `/collections/sale#products` and let Nuxt scroll?
 *
 *  - Nuxt's `scrollBehavior` for a new path waits for `page:loading:end` and
 *    then scrolls exactly once, to the hash if the element exists at that moment
 *    and to the top otherwise. Anything rendered a beat later — the homepage
 *    rows are fetched on the client — is missed, and the visitor lands at the
 *    top.
 *  - A hash in the route breaks the listings afterwards: for a same-path change
 *    the rule is `from.hash && !to.hash → top`, and Load more, filters and sort
 *    all write the query without the hash, so each would throw the shopper back
 *    to the top of the page. So the bar never puts the hash into the URL (nor
 *    through `history.replaceState`, which would also wipe vue-router's history
 *    state), and a link with a path leaves it out of its rendered `href` too:
 *    a new tab opened from it would start with the hash in its route and hit
 *    the same rule. Only a click scrolls to the section; a new tab opens at the
 *    top of the page (`announcementItemLink`).
 *
 * So the bar navigates to the path, waits until Nuxt has finished that
 * navigation INCLUDING its own scroll to the top, then waits for the section to
 * exist and scrolls to it itself. The fixed header's offset is the targets'
 * `scroll-margin-top`, not arithmetic here.
 *
 * Nothing is imported: the router, `navigateTo`, the Nuxt hook, `document` and
 * `matchMedia` are passed in, so this runs under plain Vitest with a fake DOM.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)'

/** The one section that exists on every page: the footer's newsletter sign-up. */
export const NEWSLETTER_ANCHOR = 'newsletter'

/**
 * The footer's one responsive e-mail field. Focusing it puts the cursor where
 * the visitor came to subscribe. Shared with the scratcher's "nothing left to
 * play" fallback (`useScratchFlowCore.scrollToFooterNewsletter`).
 */
export function footerNewsletterInput(doc: Document): HTMLElement | null {
  return doc.getElementById('footer-newsletter-email')
}

/** The element an anchor stands for right now, if it is on the page. */
export function findAnnouncementAnchor(anchor: string, doc: Document): HTMLElement | null {
  return anchor === NEWSLETTER_ANCHOR ? footerNewsletterInput(doc) : doc.getElementById(anchor)
}

/**
 * A location as "the page the visitor is on": no hash, no trailing slash. The
 * query stays — `/sale?page=2` and `/sale` are different pages of a listing.
 */
export function announcementPageKey(fullPath: string): string {
  const withoutHash = fullPath.split('#')[0] ?? ''
  return withoutHash.replace(/\/+(?=\?|$)/, '') || '/'
}

export interface AnnouncementLinkEnv {
  doc: Document
  matchesMedia: (query: string) => boolean
  /** The router's current `fullPath`, read when needed rather than captured. */
  currentPath: () => string
  /** `navigateTo`. */
  navigate: (target: string) => Promise<unknown>
  /** `nuxtApp.hooks.hookOnce('page:loading:end', …)`, returning its unhook. */
  onPageLoadingEnd: (callback: () => void) => () => void
  requestFrame: (callback: () => void) => void
  /** Aborted by a newer click on the bar, or when the bar unmounts. */
  signal?: AbortSignal
}

export interface AnnouncementLinkTimings {
  /** Longest wait for the new page to finish loading before looking anyway. */
  pageLoadMs: number
  /** Longest wait for the section to appear, for content rendered after the page. */
  targetMs: number
  /** When the scroll is checked once more, for sections above it still growing. */
  recheckMs: number
}

const DEFAULT_TIMINGS: AnnouncementLinkTimings = { pageLoadMs: 3000, targetMs: 4000, recheckMs: 500 }

/**
 * An unmodified click on a PAGE message. `target` is the localized path to go
 * to (null: stay on this page), `anchor` the section to scroll to on arrival.
 */
export async function followAnnouncementPageLink(
  link: { target: string | null, anchor: string | null },
  env: AnnouncementLinkEnv,
  timings: AnnouncementLinkTimings = DEFAULT_TIMINGS,
): Promise<void> {
  const aborted = () => env.signal?.aborted === true
  const from = announcementPageKey(env.currentPath())
  const elsewhere = link.target !== null && announcementPageKey(link.target) !== from

  if (link.target !== null && (elsewhere || !link.anchor)) {
    // Listening BEFORE navigating: for a query-only change on a page keyed by
    // its path, Nuxt fires the hook on the next tick, which can come before
    // `navigateTo` has even resolved.
    let unhook = () => {}
    const loaded = new Promise<void>((resolve) => {
      unhook = env.onPageLoadingEnd(resolve)
    })
    try {
      await env.navigate(link.target)
    }
    catch {
      unhook()
      return
    }
    // Without a section the navigation was the whole click, as for any link.
    // With one, a route that has not moved means a guard refused the navigation,
    // and whatever section the page left behind has is not the one asked for.
    if (!link.anchor || announcementPageKey(env.currentPath()) === from) {
      unhook()
      return
    }
    await settleWithin(loaded, timings.pageLoadMs)
    unhook()
    // Nuxt scrolls a new page to the top in a frame requested from that same
    // hook, which runs after this one; two frames later that scroll has been
    // issued, so ours comes after it rather than being undone by it.
    await nextFrame(env)
    await nextFrame(env)
  }

  if (!link.anchor || aborted())
    return

  const arrivedAt = announcementPageKey(env.currentPath())
  const el = await waitForAnnouncementAnchor(link.anchor, env, timings.targetMs)
  // The visitor clicked on elsewhere meanwhile: this scroll is no longer wanted.
  if (!el || aborted() || announcementPageKey(env.currentPath()) !== arrivedAt)
    return
  await scrollToAnnouncementAnchor(el, link.anchor, env, timings.recheckMs)
}

/**
 * The anchor's element once it exists, or null after `timeoutMs` (or on abort).
 * A section fetched on the client appears some time after the page itself.
 */
export function waitForAnnouncementAnchor(
  anchor: string,
  env: Pick<AnnouncementLinkEnv, 'doc' | 'matchesMedia' | 'signal'>,
  timeoutMs: number,
): Promise<HTMLElement | null> {
  const find = () => findAnnouncementAnchor(anchor, env.doc)
  const found = find()
  if (found || timeoutMs <= 0 || env.signal?.aborted)
    return Promise.resolve(found)

  return new Promise((resolve) => {
    let done = false
    const Observer = env.doc.defaultView?.MutationObserver
    const observer = Observer ? new Observer(onMutation) : null
    const timer = setTimeout(() => finish(find()), timeoutMs)
    const onAbort = () => finish(null)

    function onMutation(): void {
      const el = find()
      if (el)
        finish(el)
    }

    function finish(el: HTMLElement | null): void {
      if (done)
        return
      done = true
      observer?.disconnect()
      clearTimeout(timer)
      env.signal?.removeEventListener('abort', onAbort)
      resolve(el)
    }

    env.signal?.addEventListener('abort', onAbort, { once: true })
    observer?.observe(env.doc.documentElement, { childList: true, subtree: true })
  })
}

/**
 * Scroll a section into view, check once more after `recheckMs`, and move focus
 * to it so a keyboard or screen-reader visitor continues from there instead of
 * from the bar at the top of the page.
 */
export async function scrollToAnnouncementAnchor(
  el: HTMLElement,
  anchor: string,
  env: Pick<AnnouncementLinkEnv, 'doc' | 'matchesMedia' | 'signal'>,
  recheckMs: number,
): Promise<void> {
  const options: ScrollIntoViewOptions = {
    // The newsletter field sits in the footer, near the end of the page: centred
    // it clears the fixed header without a scroll margin, as the scratcher's
    // fallback has always placed it. Sections align their top, less their
    // `scroll-margin-top`.
    block: anchor === NEWSLETTER_ANCHOR ? 'center' : 'start',
    // `instant`, not `auto`: `<html>` carries `scroll-smooth`, and `auto` defers
    // to it.
    behavior: env.matchesMedia(REDUCED_MOTION_QUERY) ? 'instant' : 'smooth',
  }
  el.scrollIntoView(options)

  // Content still loading above the section (lazy rows, images without a
  // reserved height) can push it down while the scroll runs. One more pass puts
  // it back — unless the visitor has taken over, which only a person does.
  const interrupted = watchForUserScroll(env.doc)
  await sleep(recheckMs, env.signal)
  interrupted.stop()
  if (env.signal?.aborted || !el.isConnected)
    return
  if (!interrupted.happened())
    el.scrollIntoView(options)

  if (!el.hasAttribute('tabindex') && el.tabIndex < 0)
    el.setAttribute('tabindex', '-1')
  el.focus({ preventScroll: true })
}

/** Resolves when `promise` does, or after `ms`, whichever comes first. */
function settleWithin(promise: Promise<void>, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const cap = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms)
  })
  return Promise.race([promise, cap]).finally(() => clearTimeout(timer))
}

function nextFrame(env: Pick<AnnouncementLinkEnv, 'requestFrame'>): Promise<void> {
  return new Promise(resolve => env.requestFrame(() => resolve()))
}

function sleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(done, ms)
    signal?.addEventListener('abort', done, { once: true })
    function done(): void {
      clearTimeout(timer)
      signal?.removeEventListener('abort', done)
      resolve()
    }
  })
}

/** Input that means the visitor is scrolling (or about to) on their own. */
const USER_SCROLL_EVENTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const

function watchForUserScroll(doc: Document): { happened: () => boolean, stop: () => void } {
  let happened = false
  const onInput = () => {
    happened = true
  }
  for (const type of USER_SCROLL_EVENTS)
    doc.addEventListener(type, onInput, { capture: true, passive: true })
  return {
    happened: () => happened,
    stop: () => {
      for (const type of USER_SCROLL_EVENTS)
        doc.removeEventListener(type, onInput, { capture: true })
    },
  }
}
